import { Button, TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
  getOfferDefaultsConfig,
  getOperarioMobileConfig,
  updateOfferDefaultsConfig,
  updateOperarioMobileConfig,
} from "../../../../api/generalConfig";
import { alertError, alertSuccess } from "../../../../utils/logger";
import Editor from "../../../components/editor/Editor";
import { useSkeleton } from "../../../hooks/useSkeleton";

function getEmptyOfferDefaults() {
  return {
    id: null,
    cabecera_descripcion: "",
    pie_condiciones_venta: "",
  };
}

export default function GeneralConfigPage() {
  const [selectedTab, setSelectedTab] = useState("ofertas");
  const [offerDefaults, setOfferDefaults] = useState(getEmptyOfferDefaults());
  const [operarioMobileConfig, setOperarioMobileConfig] = useState({
    id: null,
    url_base_acceso_movil_desarrollo: "",
    url_base_acceso_movil_produccion: "https://field.neo4.es",
  });
  const [saving, setSaving] = useState(false);
  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;
  const { isLoading, disableLoading, ContentSkeleton } = useSkeleton();

  useEffect(() => {
    Promise.all([getOfferDefaultsConfig(accessToken), getOperarioMobileConfig(accessToken)])
      .then(([offerResponse, operariosResponse]) => {
        if (offerResponse.status === 200) {
          setOfferDefaults({ ...getEmptyOfferDefaults(), ...offerResponse.data });
        }
        if (operariosResponse.status === 200) {
          setOperarioMobileConfig({
            id: null,
            url_base_acceso_movil_desarrollo: "",
            url_base_acceso_movil_produccion: "https://field.neo4.es",
            ...operariosResponse.data,
          });
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido obtener la configuración general.",
        });
      })
      .finally(disableLoading);
  }, [accessToken, disableLoading]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedTab === "ofertas") {
        await updateOfferDefaultsConfig(offerDefaults, accessToken);
      } else if (selectedTab === "operarios") {
        await updateOperarioMobileConfig(operarioMobileConfig, accessToken);
      }
      alertSuccess({
        title: "Guardado",
        customMessage:
          selectedTab === "ofertas"
            ? "Los valores por defecto de las ofertas se han guardado."
            : "La URL de acceso móvil de operarios se ha guardado.",
      });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se ha podido guardar la configuración general.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title="Configuración general" />
        <CardBody>
          <div className="d-flex border-bottom" style={{ gap: "1.5rem" }}>
          <button
            type="button"
            className="border-0 bg-transparent font-weight-bolder pb-3"
            onClick={() => setSelectedTab("ofertas")}
            style={{ color: selectedTab === "ofertas" ? "#321ACB" : "#7E8299", borderBottom: selectedTab === "ofertas" ? "2px solid #321ACB" : "2px solid transparent" }}
          >
            Ofertas
          </button>
          <button
            type="button"
            className="border-0 bg-transparent font-weight-bolder pb-3"
            onClick={() => setSelectedTab("operarios")}
            style={{ color: selectedTab === "operarios" ? "#321ACB" : "#7E8299", borderBottom: selectedTab === "operarios" ? "2px solid #321ACB" : "2px solid transparent" }}
          >
            Operarios
          </button>
          </div>
        </CardBody>
      </Card>

      <Card className="mt-5">
        <CardHeader title={selectedTab === "ofertas" ? "Ofertas" : "Operarios"}>
          <CardHeaderToolbar>
            <Button color="primary" variant="contained" disabled={saving} onClick={handleSave}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>

        {selectedTab === "ofertas" && (
          <div style={{ maxWidth: "980px" }}>
            <div className="font-weight-bolder text-dark mb-2">Cabecera por defecto</div>
            <div className="text-muted mb-3">Se incluirá en las nuevas ofertas. Podrá editarse individualmente después.</div>
            <Editor
              body={offerDefaults.cabecera_descripcion || ""}
              setBody={(cabecera_descripcion) => setOfferDefaults((current) => ({ ...current, cabecera_descripcion }))}
              placeholder="Texto de cabecera de la oferta"
              lang="es"
            />

            <div className="font-weight-bolder text-dark mt-5 mb-2">Pie y condiciones de venta por defecto</div>
            <div className="text-muted mb-3">Se incluirá en las nuevas ofertas. Podrá editarse individualmente después.</div>
            <Editor
              body={offerDefaults.pie_condiciones_venta || ""}
              setBody={(pie_condiciones_venta) => setOfferDefaults((current) => ({ ...current, pie_condiciones_venta }))}
              placeholder="Condiciones de venta de la oferta"
              lang="es"
            />
          </div>
        )}

        {selectedTab === "operarios" && (
          <div style={{ maxWidth: "760px" }}>
            <div className="font-weight-bolder text-dark mb-2">Acceso móvil de operarios</div>
            <div className="text-muted mb-4">
              Define los destinos de la aplicación de campo. Cada enlace se generará como <code>URL/acceso/token</code> y la API elegirá la URL del entorno donde se esté ejecutando.
            </div>
            <TextField
              label="URL base de desarrollo"
              value={operarioMobileConfig.url_base_acceso_movil_desarrollo || ""}
              onChange={(event) => {
                const url_base_acceso_movil_desarrollo = event.target.value;
                setOperarioMobileConfig((current) => ({ ...current, url_base_acceso_movil_desarrollo }));
              }}
              placeholder="https://movil.tudominio.com"
              helperText="Usa localhost con el puerto de la aplicación de campo. No incluyas /acceso ni el token."
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
            <TextField
              label="URL base de producción"
              value={operarioMobileConfig.url_base_acceso_movil_produccion || ""}
              onChange={(event) => {
                const url_base_acceso_movil_produccion = event.target.value;
                setOperarioMobileConfig((current) => ({ ...current, url_base_acceso_movil_produccion }));
              }}
              helperText="Destino público de Field."
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
            />
          </div>
        )}
        </CardBody>
      </Card>
    </>
  );
}
